// Generate Messages Trend Data from API response
export const generateMessagesTrendData = (apiData, messageTypeFilter = [], directionFilter = null, manufacturer = 'toyota', isDrilldownData = false, sbidFilter = []) => {
  if (!apiData || apiData.length === 0) {
    return {
      categories: [],
      series: []
    };
  }

  let filteredData = apiData;
  
  // Apply message type filter locally if provided
  if (!isDrilldownData && messageTypeFilter && messageTypeFilter.length > 0) {
    filteredData = filteredData.filter(item => 
      messageTypeFilter.includes(item.Category)
    );
  }
  
  // Apply sbid filter locally if provided
  if (isDrilldownData && sbidFilter && sbidFilter.length > 0) {
    filteredData = filteredData.filter(item => 
      sbidFilter.includes(item.Category)
    );
  }

  // Apply direction filter locally if provided
  if (directionFilter) {
    const isRaymond = manufacturer.toLowerCase().startsWith('raymond');
    filteredData = filteredData.filter(item => {
      if (isRaymond) {
        // Raymond uses 'Inbound' and 'Outbound'
        return directionFilter === 'inbound' 
          ? item.Direction === 'Inbound' 
          : item.Direction === 'Outbound';
      } else {
        // Toyota uses 'D2C' (inbound) and 'C2D' (outbound)
        return directionFilter === 'inbound' 
          ? item.Direction === 'D2C' 
          : item.Direction === 'C2D';
      }
    });
  }

  if (filteredData.length === 0) {
    return {
      categories: [],
      series: []
    };
  }

  // Special case: when drilldown is active and no sbid filter, show all categories as single column
  if (isDrilldownData && sbidFilter.length === 0) {
    // Step 1: Get unique dates and sort them, excluding today
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of today for accurate comparison
    
    const uniqueDates = [...new Set(filteredData.map(item => item.dateVal))]
      .filter(dateStr => {
        const itemDate = new Date(dateStr);
        itemDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
        return itemDate.getTime() < today.getTime(); // Exclude today
      })
      .sort();
    
    // Step 2: Format dates for categories
    const categories = uniqueDates.map(dateStr => {
      const date = new Date(dateStr);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[date.getMonth()]} ${date.getDate()}`;
    });
    
    // Step 3: Calculate total count per date
    const data = uniqueDates.map(dateVal => {
      return filteredData.filter(item => item.dateVal === dateVal).reduce((sum, item) => sum + (item.countVal || 0), 0);
    });
    
    return {
      categories,
      series: [{
        name: 'All Categories',
        data,
        type: 'column'
      }]
    };
  }

  // Transform API response to chart format
  // Expected API response: [{ dateVal: 'YYYY-MM-DDTHH:mm:ss.sssZ', ED: 'error description', countVal: number }, ...]
  // Data is grouped by dateVal and ED, so we need to pivot it for the stacked chart
  
  // Step 1: Get unique dates and sort them, excluding today
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of today for accurate comparison
  
  const uniqueDates = [...new Set(filteredData.map(item => item.dateVal))]
    .filter(dateStr => {
      const itemDate = new Date(dateStr);
      itemDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
      return itemDate.getTime() < today.getTime(); // Exclude today
    })
    .sort();
  
  // Step 2: Format dates for categories
  const categories = uniqueDates.map(dateStr => {
    const date = new Date(dateStr);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}`;
  });
  
  // Step 3: Get unique error descriptions (ED) for series
  const uniqueEDs = [...new Set(filteredData.map(item => item.ErrorDescription || 'Unknown'))].sort();
  
  // Step 4: Create a lookup map for quick access: dateVal + ED -> countVal
  const dataMap = new Map();
  filteredData.forEach(item => {
    const key = `${item.dateVal}_${item.ErrorDescription || 'Unknown'}`;
    dataMap.set(key, item.countVal || 0);
  });
  
  // Step 5: Build series array - one series per ED (error description)
  const series = uniqueEDs.map(ed => {
    // For each ED, create an array of counts matching each date
    const data = uniqueDates.map(dateVal => {
      const key = `${dateVal}_${ed}`;
      return dataMap.get(key) || 0; // Return 0 if no data for this date/ED combination
    });
    
    return {
      name: ed,
      data: data,
      type: 'column'
    };
  });

  // Sort series in descending order by total count (highest count at bottom of stack)
  series.sort((a, b) => {
    const sumA = a.data.reduce((sum, val) => sum + val, 0);
    const sumB = b.data.reduce((sum, val) => sum + val, 0);
    return sumB - sumA; // Descending order
  });

  return {
    categories,
    series
  };
};

// Generate VNC Trend Data from API response
export const generateVncTrendData = (apiData) => {
  if (!apiData || apiData.length === 0) {
    return {
      categories: [],
      series: [
        {
          name: 'VNC Count',
          data: [],
          type: 'line'
        }
      ]
    };
  }

  // Transform API response to chart format
  // Expected API response: [{ CommunicationDate: 'YYYY-MM-DD', NotCommunicatingCount: number }, ...]
  // Data comes in DESC order (most recent first), so we need to reverse it for chronological display
  const reversedData = [...apiData].reverse();
  
  const categories = reversedData.map(item => {
    // Convert date string to 'MMM DD' format
    const date = new Date(item.CommunicationDate);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}`;
  });
  
  const counts = reversedData.map(item => item.NotCommunicatingCount);

  return {
    categories,
    series: [
      {
        name: 'VNC Count',
        data: counts,
        type: 'line'
      }
    ]
  };
};

// Hardcoded Telematics Error Data (7 days)
// TODO: Replace with API call in the future
export const generateTelematicsErrorData = () => {
  return {
    categories: [
      'Sep 19', 'Sep 20', 'Sep 21', 'Sep 22', 'Sep 23', 'Sep 24', 'Sep 25'
    ],
    series: [
      {
        name: 'Connection Timeout',
        data: [12, 15, 8, 18, 14, 22, 16]
      },
      {
        name: 'Data Corruption',
        data: [5, 8, 12, 7, 9, 11, 6]
      },
      {
        name: 'Signal Loss',
        data: [8, 6, 14, 10, 7, 9, 12]
      },
      {
        name: 'Authentication Failed',
        data: [3, 4, 2, 6, 5, 8, 4]
      },
      {
        name: 'Network Error',
        data: [7, 9, 5, 11, 8, 6, 10]
      },
      {
        name: 'Hardware Malfunction',
        data: [2, 1, 4, 3, 2, 5, 3]
      }
    ]
  };
};

// Generate Telematics Error Data from API response
export const generateTelematicsErrorDataFromAPI = (apiData) => {
  if (!apiData || apiData.length === 0) {
    return {
      categories: [],
      series: []
    };
  }

  // Transform API response to chart format
  // Expected API response: [{ CommunicationDate: 'YYYY-MM-DDTHH:mm:ss.sssZ', teid: number, DescriptionEnglish: string, VehicleCount: number }, ...]
  
  // Get unique dates and sort them
  const uniqueDates = [...new Set(apiData.map(item => item.CommunicationDate))].sort();
  
  // Format dates for display
  const categories = uniqueDates.map(dateStr => {
    const date = new Date(dateStr);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}`;
  });

  // Get unique error descriptions
  const uniqueErrors = [...new Set(apiData.map(item => item.DescriptionEnglish || `Error ${item.teid}`))];

  // Create series data for each error type
  const series = uniqueErrors.map(errorDesc => {
    const data = uniqueDates.map(date => {
      // Find the matching record for this date and error type
      const record = apiData.find(item => 
        item.CommunicationDate === date && 
        (item.DescriptionEnglish || `Error ${item.teid}`) === errorDesc
      );
      return record ? record.VehicleCount : 0;
    });

    return {
      name: errorDesc,
      data: data
    };
  });

  // Sort series in descending order by total count (highest count at bottom of stack)
  series.sort((a, b) => {
    const sumA = a.data.reduce((sum, val) => sum + val, 0);
    const sumB = b.data.reduce((sum, val) => sum + val, 0);
    return sumB - sumA; // Descending order
  });

  return {
    categories,
    series
  };
};

// Generate Raymond Fault Codes Data from API response
export const generateRaymondFaultCodesDataFromAPI = (apiData, selectedDescriptions = []) => {
  if (!apiData || apiData.length === 0) {
    return {
      categories: [],
      series: []
    };
  }

  // Get unique dates and sort them
  const uniqueDates = [...new Set(apiData.map(item => item.CommunicationDate))].sort();
  
  // Format dates for display
  const categories = uniqueDates.map(dateStr => {
    // Parse the date string correctly (Druid returns ISO format)
    const date = new Date(dateStr);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // Use UTC methods to avoid timezone issues
    return `${monthNames[date.getUTCMonth()]} ${date.getUTCDate()}`;
  });

  if (selectedDescriptions.length > 0) {
    // Show individual stacked data for selected descriptions
    // Get unique fault descriptions with their error values
    const uniqueFaults = [...new Set(apiData.map(item => {
      const desc = item.DescriptionEnglish || `SBID ${item.SBID}`;
      const ev = item.ErrorValue || '';
      return ev ? `${desc} (${ev})` : desc;
    }))];

    // Create series data for each fault type
    const series = uniqueFaults.map(faultDesc => {
      const data = uniqueDates.map(date => {
        // Find matching records for this date and fault type
        const records = apiData.filter(item => {
          const desc = item.DescriptionEnglish || `SBID ${item.SBID}`;
          const ev = item.ErrorValue || '';
          const itemFault = ev ? `${desc} (${ev})` : desc;
          return item.CommunicationDate === date && itemFault === faultDesc;
        });
        
        // Sum up vehicle counts for this fault on this date
        return records.reduce((sum, record) => sum + (record.VehicleCount || 0), 0);
      });

      return {
        name: faultDesc,
        data: data
      };
    });

    // Sort series in descending order by total count (highest count at bottom of stack)
    series.sort((a, b) => {
      const sumA = a.data.reduce((sum, val) => sum + val, 0);
      const sumB = b.data.reduce((sum, val) => sum + val, 0);
      return sumB - sumA; // Descending order
    });

    return {
      categories,
      series
    };
  } else {
    // Club the data into a single series
    const data = uniqueDates.map(date => {
      return apiData.filter(item => item.CommunicationDate === date).reduce((sum, item) => sum + (item.VehicleCount || 0), 0);
    });

    return {
      categories,
      series: [{
        name: 'All Fault Codes',
        data,
        type: 'column'
      }]
    };
  }
};

// Generate Raymond Error Codes Data from API response
export const generateRaymondErrorCodesDataFromAPI = (apiData, selectedDescriptions = []) => {
  if (!apiData || apiData.length === 0) {
    return {
      categories: [],
      series: []
    };
  }

  // Get unique dates and sort them
  const uniqueDates = [...new Set(apiData.map(item => item.CommunicationDate))].sort();
  
  // Format dates for display
  const categories = uniqueDates.map(dateStr => {
    // Parse the date string correctly (Druid returns ISO format)
    const date = new Date(dateStr);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // Use UTC methods to avoid timezone issues
    return `${monthNames[date.getUTCMonth()]} ${date.getUTCDate()}`;
  });

  if (selectedDescriptions.length > 0) {
    // Show individual stacked data for selected descriptions
    // Get unique error descriptions with their error values
    const uniqueErrors = [...new Set(apiData.map(item => {
      const desc = item.DescriptionEnglish || `SBID ${item.SBID}`;
      const ev = item.ErrorValue || '';
      return ev ? `${desc} (${ev})` : desc;
    }))];

    // Create series data for each error type
    const series = uniqueErrors.map(errorDesc => {
      const data = uniqueDates.map(date => {
        // Find matching records for this date and error type
        const records = apiData.filter(item => {
          const desc = item.DescriptionEnglish || `SBID ${item.SBID}`;
          const ev = item.ErrorValue || '';
          const itemError = ev ? `${desc} (${ev})` : desc;
          return item.CommunicationDate === date && itemError === errorDesc;
        });
        
        // Sum up vehicle counts for this error on this date
        return records.reduce((sum, record) => sum + (record.VehicleCount || 0), 0);
      });

      return {
        name: errorDesc,
        data: data
      };
    });

    // Sort series in descending order by total count (highest count at bottom of stack)
    series.sort((a, b) => {
      const sumA = a.data.reduce((sum, val) => sum + val, 0);
      const sumB = b.data.reduce((sum, val) => sum + val, 0);
      return sumB - sumA; // Descending order
    });

    return {
      categories,
      series
    };
  } else {
    // Club the data into a single series
    const data = uniqueDates.map(date => {
      return apiData.filter(item => item.CommunicationDate === date).reduce((sum, item) => sum + (item.VehicleCount || 0), 0);
    });

    return {
      categories,
      series: [{
        name: 'All Error Codes',
        data,
        type: 'column'
      }]
    };
  }
};

